import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const PhotoLightbox = ({ photos, index, onChange, onClose, title }) => {
  const touchX = useRef(null);
  const thumbsRef = useRef(null);
  const n = photos.length;
  const go = (d) => onChange((index + d + n) % n);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { onChange((index + 1) % n); }
      if (e.key === "ArrowLeft") { onChange((index - 1 + n) % n); }
      if (e.key === "Escape") { onClose(); }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [index, n, onChange, onClose]);

  useEffect(() => {
    const el = thumbsRef.current?.children[index];
    if (el) { el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); }
  }, [index]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) { return; }
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) { go(dx < 0 ? 1 : -1); }
    touchX.current = null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black text-white" data-testid="photo-lightbox"
      role="dialog" aria-modal="true" aria-label={`${title} photos`}>
      <div className="flex items-center justify-between px-4 py-3 sm:px-8">
        <span className="text-sm font-medium tracking-wide text-white/80" data-testid="lightbox-counter">{index + 1} / {n}</span>
        <span className="hidden truncate px-4 text-sm text-white/60 sm:block">{title}</span>
        <button onClick={onClose} data-testid="lightbox-close" aria-label="Close"
          className="rounded-full p-2 transition-colors hover:bg-white/10"><X size={22} /></button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 sm:px-20"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
        <img key={index} src={photos[index]} alt={`${title} photo ${index + 1}`} data-testid="lightbox-image"
          className="max-h-full max-w-full select-none object-contain animate-in fade-in duration-300" draggable={false} />
        {n > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="lightbox-prev" aria-label="Previous photo"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-black shadow-lg transition-transform hover:scale-105 sm:left-6">
              <ChevronLeft size={22} />
            </button>
            <button onClick={() => go(1)} data-testid="lightbox-next" aria-label="Next photo"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-black shadow-lg transition-transform hover:scale-105 sm:right-6">
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      <div ref={thumbsRef} className="flex gap-2 overflow-x-auto px-4 py-4" data-testid="lightbox-thumbs">
        {photos.map((src, i) => (
          <button key={i} onClick={() => onChange(i)} data-testid={`lightbox-thumb-${i}`}
            className={`h-14 w-20 shrink-0 overflow-hidden rounded-md transition-opacity ${i === index ? "opacity-100 ring-2 ring-white" : "opacity-50 hover:opacity-90"}`}>
            <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
};
